import * as enum_ from "lib/enum"
import * as valid from "lib/valid"
import * as result from "lib/result"
import * as log from "lib/log"
import { MatchHandlers } from "lib/enum"

type optionMap<T> = {
	Some: T
	None: null | undefined
}

export type OptHandler<T, R> = {
	Some: (value: T) => R
	None: () => R
}

// Create a Member type for Optional values
export class Opt<T> extends enum_.Variant<optionMap<T>> {
	/* eslint-disable-next-line */
	//@ts-ignore
	override Match<R>(handlers: OptHandler<T, R>): R {
		// Cast to the general handler type expected by the parent class
		return super.Match(handlers as MatchHandlers<optionMap<T>, R>)
	}

	Expect(message: string): T {
		if (!this.IsSome()) {
			try {
				log.Fatal(message)
			} catch (e) {
				console.error("Error calling log.Fatal:", e)
				throw new Error(message) // Fallback
			}
		}
		return this.Data as T
	}

	UnwrapOrElse(fn: () => T): T {
		return this.IsSome() ? (this.Data as T) : fn()
	}

	IfSome<U>(fn: (val: T) => U): Opt<U> {
		if (this.Is("Some")) {
			return Some(fn(this.Data as T))
		}
		return None<U>()
	}

	IfNone<U>(fn: (val: T) => U): Opt<U> {
		if (this.Is("None")) {
			return Some(fn(this.Data as T))
		}
		return None<U>()
	}

	Unwrap(): T {
		if (!this.IsSome()) {
			log.Fatal("Called unwrap on None value")
		}
		return this.Data as T
	}

	Filter(fn: (value: T) => bool): Opt<T> {
		if (!this.IsSome() || !fn(this.Data as T)) {
			return None<T>()
		}
		return Some(this.Data as T)
	}

	FilterMap<U>(fn: (value: T) => Opt<U>): Opt<U> {
		if (!this.IsSome()) {
			return None<U>()
		}
		return fn(this.Data as T)
	}

	Zip<U>(other: Opt<U>): Opt<[T, U]> {
		if (!this.IsSome() || !other.IsSome()) {
			return None<[T, U]>()
		}
		return Some([this.Data as T, other.Data as U])
	}

	Or(other: Opt<T>): Opt<T> {
		return this.IsSome() ? this : other
	}

	Join<U, R>(other: Opt<U>, fn: (a: T, b: U) => R): Opt<R> {
		if (!this.IsSome() || !other.IsSome()) {
			return None<R>()
		}
		return Some(fn(this.Data as T, other.Data as U))
	}

	UnwrapOrNil(): T | undefined {
		return this.IsSome() ? (this.Data as T) : undefined
	}

	UnwrapOr(defaultValue: T): T {
		return this.IsSome() ? (this.Data as T) : defaultValue
	}

	Clone(): Opt<T> {
		if (!this.IsSome()) {
			return None<T>()
		}
		return Some(structuredClone(this.Data as T))
	}

	OrElse(fn: () => Opt<T>): Opt<T> {
		return this.IsSome() ? this : fn()
	}

	// New names for isSome and isNone with Go-style capitalization
	IsSome(): bool {
		return this.Tag.Equal("Some") && !isNil(this.Data)
	}

	IsNone(): bool {
		return !this.IsSome()
	}

	AndThen<U>(fn: (value: T) => Opt<U>): Opt<U> {
		return this.Match({
			Some: fn,
			None: () => None<U>(),
		})
	}

	IsNoneOr(predicate: (value: T) => bool): bool {
		if (!this.IsSome()) {
			return true
		}
		return predicate(this.Data as T)
	}

	IsSomeAnd(predicate: (value: T) => bool): bool {
		if (!this.IsSome()) {
			return false
		}
		return predicate(this.Data as T)
	}

	OkOr<E>(errVal: E): Result<T, E> {
		if (!this.IsSome()) {
			return result.Err(errVal)
		}
		return result.Ok(this.Data as T)
	}

	OkOrElse<E>(errFn: () => E): Result<T, E> {
		if (!this.IsSome()) {
			return result.Err(errFn())
		}
		return result.Ok(this.Data as T)
	}

	Map<U>(fn: (value: T) => U): Opt<U> {
		if (!this.IsSome()) {
			return None<U>()
		}
		return Some(fn(this.Data as T))
	}

	Tap(fn: (value: T) => void): Opt<T> {
		if (this.IsSome()) {
			fn(this.Data as T)
		}
		return this
	}
}

// Factory functions
export function Some<T>(value: T): Opt<T> {
	return new Opt<T>("Some", value)
}

export function IsOpt<T>(v: any): v is Opt<T> {
	return v instanceof Opt
}

export function AreEqual<T>(
	a: Opt<T>,
	b: Opt<T>,
	equalityFn?: (unwrappedA: T, unwrappedB: T) => boolean
): boolean {
	// If both are None, they're equal
	if (a.IsNone() && b.IsNone()) {
		return true
	}

	// If one is Some and the other is None, they're not equal
	if (a.IsSome() !== b.IsSome()) {
		return false
	}

	// Both are Some, compare their values
	return a.Match({
		Some: (valueA) => {
			return b.Match({
				Some: (valueB) => {
					// Use custom equality function if provided
					if (equalityFn) {
						return equalityFn(valueA, valueB)
					}
					return valid.AreEqual(valueA, valueB)
				},
				None: () => false, // Should never reach here due to previous check
			})
		},
		None: () => b.IsNone(), // Should never reach here due to previous check
	})
}

export function None<T>(): Opt<T> {
	return new Opt<T>("None", null as T)
}

export function FromNullable<T>(v: T | null | undefined): Opt<T> {
	if (isNil(v)) {
		return None<T>()
	}
	return Some(v)
}
